import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { DocDataWithId, Notification, NotificationBundleEntry } from '#types/System.types';
import { createCollectionStore, type CollectionStore } from '../store';

type NotificationBundleDoc = DocDataWithId & {
    docType?: 'notificationBundle';
    entries?: NotificationBundleEntry[];
    entryCount?: number;
};

type UseNotificationBundlesParams = {
    userId?: string;
    enabled: boolean;
};

const inactiveStore = createCollectionStore<NotificationBundleDoc>('users/__inactive__/notificationBundles');

export const useNotificationBundles = ({
    userId,
    enabled,
}: UseNotificationBundlesParams) => {
    const bundlesPath = userId ? `users/${userId}/notificationBundles` : undefined;
    const isActive = enabled && Boolean(bundlesPath);

    const store = useMemo<CollectionStore<NotificationBundleDoc>>(
        () => bundlesPath
            ? createCollectionStore<NotificationBundleDoc>(bundlesPath, {
                queryOptions: {
                    extraConstraints: [
                        { type: 'where', field: 'docType', op: '==', value: 'notificationBundle' },
                        { type: 'limit', n: 500 },
                    ],
                },
            })
            : inactiveStore,
        [bundlesPath],
    );

    useEffect(() => {
        if (!isActive) return;
        store.connect();
        return () => store.disconnect();
    }, [isActive, store]);

    const snap = useSnapshot(store.state);

    const bundleData = useMemo<NotificationBundleDoc[]>(() => {
        if (!isActive) return [];
        const result: NotificationBundleDoc[] = [];
        for (const id of snap.docIds) {
            const entry = snap.docs.get(id);
            if (entry) result.push({ ...(entry.data as NotificationBundleDoc), docId: id });
        }
        return result;
    }, [isActive, snap.docIds, snap.docs]);

    const derived = useMemo(() => {
        const notificationBundleDocIdByEventId = new Map<string, string>();
        const notificationEntryByEventId = new Map<string, NotificationBundleEntry>();

        const entries = bundleData.flatMap((bundle) => {
            const bundleEntries = Array.isArray(bundle.entries) ? bundle.entries : [];
            const bundleDocId = typeof bundle.docId === 'string' ? bundle.docId : undefined;

            if (bundleDocId) {
                bundleEntries.forEach((entry) => {
                    const eventId = entry?.notification?.notificationEvent?.docId;
                    if (eventId) {
                        notificationBundleDocIdByEventId.set(eventId, bundleDocId);
                        notificationEntryByEventId.set(eventId, entry);
                    }
                });
            }

            return bundleEntries;
        });

        const unreadNotifications: Notification[] = [];
        const readNotifications: Notification[] = [];

        entries.forEach((entry) => {
            if (!entry?.notification) {
                return;
            }
            if (entry.status === 'read') {
                readNotifications.push(entry.notification);
            } else {
                unreadNotifications.push(entry.notification);
            }
        });

        return {
            bundlesPath,
            entries,
            unreadNotifications,
            readNotifications,
            notificationBundleDocIdByEventId,
            notificationEntryByEventId,
        };
    }, [bundleData, bundlesPath]);

    return derived;
};
